// src/components/mensagens/QuickActions.jsx
// ============================================================================
// 🎯 COMPONENTE: QuickActions
// 📋 Função: Ações rápidas para o card de cliente (histórico, copiar, whatsapp, templates)
// 📱 Responsivo: Adaptado para mobile e desktop com feedback tátil
// ============================================================================

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  History,
  Copy,
  Check,
  Send,
  MoreVertical,
  Phone,
  MessageSquare,
  Calendar,
  Star,
  FileText,
  X
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';

/**
 * 🎯 Componente de ações rápidas
 * @param {Object} props
 * @param {Object} props.client - Dados do cliente
 * @param {Object} props.contactInfo - Informações de contato
 * @param {string} props.copiedId - ID do cliente com telefone copiado
 * @param {string} props.selectedClient - ID do cliente selecionado
 * @param {Function} props.onCopyPhone - Callback para copiar telefone
 * @param {Function} props.onWhatsApp - Callback para enviar WhatsApp
 * @param {Function} props.onTemplateSelect - Callback para selecionar template
 * @param {Function} props.onShowHistory - Callback para mostrar histórico
 * @param {Function} props.setSelectedClient - Callback para setar cliente selecionado
 * @param {string} props.primaryColor - Cor primária do tema
 * @param {boolean} props.isMobile - Se está em mobile
 */
export const QuickActions = ({
  client,
  contactInfo,
  copiedId,
  selectedClient,
  onCopyPhone,
  onWhatsApp,
  onTemplateSelect,
  onShowHistory,
  setSelectedClient,
  primaryColor,
  isMobile
}) => {
  const [touchActive, setTouchActive] = useState(false);
  const [showAllActions, setShowAllActions] = useState(false);

  const handleTouchStart = () => setTouchActive(true);
  const handleTouchEnd = () => setTouchActive(false);

  // Templates disponíveis
  const templates = [
    { id: 'proposta', label: 'Proposta', icon: FileText, color: 'blue' },
    { id: 'visita', label: 'Visita', icon: Calendar, color: 'green' },
    { id: 'lembrete', label: 'Lembrete', icon: Star, color: 'amber' }
  ];

  // Versão mobile com menu expansível
  if (isMobile) {
    return (
      <div className="flex items-center gap-1 justify-between w-full">
        {/* Ações principais sempre visíveis no mobile */}
        <div className="flex items-center gap-1">
          {/* Botão Histórico */}
          {contactInfo && (
            <button
              onClick={() => onShowHistory(client)}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition touch:active:scale-95"
              title="Ver histórico"
              aria-label="Ver histórico"
            >
              <History size={18} className="text-gray-600" />
            </button>
          )}

          {/* Botão Copiar Telefone */}
          {client.phone && (
            <button
              onClick={() => onCopyPhone(client.phone, client.id)}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition touch:active:scale-95 relative"
              title="Copiar telefone"
              aria-label="Copiar telefone"
            >
              {copiedId === client.id ? (
                <Check size={18} className="text-green-600" />
              ) : (
                <Copy size={18} className="text-gray-600" />
              )}
            </button>
          )}
        </div>

        {/* Botão WhatsApp Principal */}
        <button
          onClick={() => onWhatsApp(client, 'padrao')}
          disabled={!client.phone}
          className={`
            flex items-center gap-1 px-3 py-2 rounded-lg transition font-medium touch:active:scale-95
            ${client.phone 
              ? 'text-white shadow-sm hover:opacity-90' 
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }
          `}
          style={client.phone ? { backgroundColor: primaryColor } : {}}
          aria-label="Enviar WhatsApp"
        >
          <Send size={16} />
          <span className="sr-only">WhatsApp</span>
        </button>

        {/* Menu de Templates */}
        {client.phone && (
          <div className="relative">
            <button
              onClick={() => setSelectedClient(selectedClient === client.id ? null : client.id)}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition touch:active:scale-95"
              aria-label="Mais opções"
              aria-expanded={selectedClient === client.id}
            >
              <MoreVertical size={18} className="text-gray-600" />
            </button>

            <AnimatePresence>
              {selectedClient === client.id && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10 w-36"
                >
                  {templates.map((template) => {
                    const Icon = template.icon;
                    return (
                      <button
                        key={template.id}
                        onClick={() => {
                          onTemplateSelect(client, template.id);
                          setSelectedClient(null);
                        }}
                        className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-50 active:bg-gray-100 touch:active:bg-gray-100 text-xs flex items-center gap-2"
                      >
                        <Icon size={14} />
                        {template.label}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    );
  }

  // Versão desktop com todas as ações visíveis
  return (
    <div className="flex items-center gap-1 justify-end flex-wrap">
      {/* Botão Histórico */}
      {contactInfo && (
        <button
          onClick={() => onShowHistory(client)}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
          title="Ver histórico"
          aria-label="Ver histórico"
        >
          <History size={18} className="text-gray-600 group-hover:text-gray-800" />
        </button>
      )}

      {/* Botão Copiar Telefone */}
      {client.phone && (
        <button
          onClick={() => onCopyPhone(client.phone, client.id)}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors relative group"
          title="Copiar telefone"
          aria-label="Copiar telefone"
        >
          {copiedId === client.id ? (
            <Check size={18} className="text-green-600" />
          ) : (
            <Copy size={18} className="text-gray-600 group-hover:text-gray-800" />
          )}
        </button>
      )}

      {/* Botão WhatsApp Principal */}
      <button
        onClick={() => onWhatsApp(client, 'padrao')}
        disabled={!client.phone}
        className={`
          flex items-center gap-1 px-4 py-2 rounded-lg transition font-medium
          ${client.phone 
            ? 'text-white shadow-sm hover:opacity-90' 
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }
        `}
        style={client.phone ? { backgroundColor: primaryColor } : {}}
        aria-label="Enviar WhatsApp padrão"
      >
        <Send size={18} />
        <span>WhatsApp</span>
      </button>

      {/* Menu de Templates */}
      {client.phone && (
        <div className="relative">
          <button
            onClick={() => setSelectedClient(selectedClient === client.id ? null : client.id)}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            aria-label="Mais templates"
            aria-expanded={selectedClient === client.id}
          >
            <MoreVertical size={18} className="text-gray-600" />
          </button>

          <AnimatePresence>
            {selectedClient === client.id && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10 w-48"
              >
                {templates.map((template) => {
                  const Icon = template.icon;
                  const colorClasses = {
                    blue: 'text-blue-600',
                    green: 'text-green-600',
                    amber: 'text-amber-600'
                  };

                  return (
                    <button
                      key={template.id}
                      onClick={() => {
                        onTemplateSelect(client, template.id);
                        setSelectedClient(null);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors text-sm flex items-center gap-2"
                    >
                      <Icon size={16} className={colorClasses[template.color]} />
                      {template.label}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

/**
 * 🎯 Componente de ação única (botão com tooltip)
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Ícone
 * @param {string} props.label - Label do botão
 * @param {Function} props.onClick - Callback de clique
 * @param {string} props.color - Cor do botão
 * @param {boolean} props.disabled - Desabilitado
 * @param {string} props.tooltip - Tooltip (opcional)
 */
export const ActionButton = ({
  icon,
  label,
  onClick,
  color = 'gray',
  disabled = false,
  tooltip
}) => {
  const isMobile = useIsMobile();
  const [showTooltip, setShowTooltip] = useState(false);

  const colorClasses = {
    blue: 'text-blue-600 hover:bg-blue-50 border-blue-200',
    green: 'text-green-600 hover:bg-green-50 border-green-200',
    amber: 'text-amber-600 hover:bg-amber-50 border-amber-200',
    red: 'text-red-600 hover:bg-red-50 border-red-200',
    gray: 'text-gray-600 hover:bg-gray-50 border-gray-200'
  };

  return (
    <div className="relative">
      <button
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`
          p-2 rounded-lg border transition-all touch:active:scale-95
          ${colorClasses[color] || colorClasses.gray}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        aria-label={label}
      >
        {icon}
      </button>

      {/* Tooltip - apenas desktop */}
      {!isMobile && tooltip && showTooltip && !disabled && (
        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none z-20">
          {tooltip}
          <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
        </div>
      )}
    </div>
  );
};

/**
 * 🎯 Componente de menu de ações (dropdown)
 * @param {Object} props
 * @param {Array} props.actions - Lista de ações
 * @param {React.ReactNode} props.trigger - Botão trigger
 * @param {string} props.position - Posição do menu
 */
export const ActionsMenu = ({ actions = [], trigger, position = 'right' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const positionClasses = {
    right: 'right-0',
    left: 'left-0',
    center: 'left-1/2 -translate-x-1/2'
  };

  return (
    <div className="relative" ref={menuRef}>
      <div onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`absolute ${positionClasses[position]} mt-2 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10 min-w-[160px]`}
          >
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={() => {
                  action.onClick?.();
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors text-sm flex items-center gap-2"
              >
                {action.icon && <span className="text-gray-500">{action.icon}</span>}
                {action.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================================
// 🎯 EXPORTAÇÃO PADRÃO
// ============================================================================
export default QuickActions;